import React, { useState } from 'react';
import { Check, ChevronDown, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useContent } from '../context/ContentContext';
import { useLang } from '../context/LanguageContext';
import Reveal from './Reveal';

const FALLBACK = {
  section_title: 'Professionell – und trotzdem bezahlbar',
  section_subtitle: 'Keine hohen Einstiegskosten. Alle Projekte können flexibel erweitert werden.',
  setup: { price: '500', currency: 'CHF', description: 'Einmalig: Aufbau', includes: [], footnote: '' },
  monthly: { priceFrom: '150', currency: 'CHF', description: 'Monatlich: Betrieb & Betreuung', note: '', includes: [], footnote: '' },
  cta_title: 'Was kostet Sie heute am meisten Zeit?',
  cta_subtitle: 'Erzählen Sie es uns. Wir zeigen Ihnen verständlich, was sich automatisieren lässt – und was es kostet.',
  cta_button: 'Beratung anfragen',
};

const IncludesToggle = ({ open, onToggle, items, dark, testId }) => {
  const { t } = useLang();
  return (
  <div>
    <button
      onClick={onToggle}
      className={`flex items-center gap-2 text-sm font-medium ${dark ? 'text-white' : 'text-[#0071E3]'} hover:underline`}
      data-testid={testId}
    >
      {t('pricing.included')}
      <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={2} />
    </button>
    {open && (
      <div className="space-y-3 mt-5">
        {(items || []).map((item, index) => (
          <div key={index} className="flex items-start space-x-3">
            <Check className={`w-5 h-5 ${dark ? 'text-white' : 'text-[#0071E3]'} mt-0.5 flex-shrink-0`} strokeWidth={2.5} />
            <span className={dark ? 'text-white' : 'text-[#1D1D1F]'}>{item}</span>
          </div>
        ))}
      </div>
    )}
  </div>
  );
};

const Pricing = () => {
  const data = useContent('pricing', FALLBACK);
  const { t } = useLang();
  const setup = data.setup || FALLBACK.setup;
  const monthly = data.monthly || FALLBACK.monthly;
  const [showSetup, setShowSetup] = useState(false);
  const [showMonthly, setShowMonthly] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="py-24 px-6 bg-[#F5F5F7]">
      <div className="max-w-[1080px] mx-auto">
        <Reveal className="text-center mb-20">
          <p className="text-xs font-semibold text-[#0071E3] tracking-widest uppercase mb-4">{t('pricing.eyebrow')}</p>
          <h2 className="text-4xl md:text-5xl font-semibold text-[#1D1D1F] mb-4 tracking-tight" data-testid="pricing-title">
            {data.section_title}
          </h2>
          <p className="text-xl text-[#86868B] max-w-2xl mx-auto" data-testid="pricing-subtitle">
            {data.section_subtitle}
          </p>
        </Reveal>

        <Reveal delay={100}>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-3xl p-10" data-testid="pricing-setup-card">
            <div className="mb-6">
              <div className="text-sm text-[#86868B] mb-2">{setup.description}</div>
              <div className="flex items-baseline">
                <span className="text-5xl font-semibold text-[#1D1D1F]">{setup.price}</span>
                <span className="text-2xl text-[#86868B] ml-2">{setup.currency}</span>
              </div>
            </div>

            {setup.footnote && (
              <p className="text-sm text-[#86868B] mb-6">{setup.footnote}</p>
            )}

            <IncludesToggle
              open={showSetup}
              onToggle={() => setShowSetup(!showSetup)}
              items={setup.includes}
              dark={false}
              testId="pricing-setup-toggle"
            />
          </div>

          <div className="bg-[#1D1D1F] rounded-3xl p-10 relative" data-testid="pricing-monthly-card">
            <div className="mb-6">
              <div className="text-sm text-white/60 mb-2">{monthly.description}</div>
              <div className="flex items-baseline">
                <span className="text-xl text-white/60 mr-2">{t('pricing.from')}</span>
                <span className="text-5xl font-semibold text-white">{monthly.priceFrom}</span>
                <span className="text-2xl text-white/60 ml-2">{monthly.currency}</span>
              </div>
              {monthly.note && <p className="text-sm text-white/60 mt-2">{monthly.note}</p>}
            </div>

            {monthly.footnote && (
              <p className="text-sm text-white/70 mb-6">{monthly.footnote}</p>
            )}

            <IncludesToggle
              open={showMonthly}
              onToggle={() => setShowMonthly(!showMonthly)}
              items={monthly.includes}
              dark={true}
              testId="pricing-monthly-toggle"
            />
          </div>
        </div>
        </Reveal>

        <Reveal className="mt-16">
        <div className="bg-[#0071E3] rounded-3xl px-8 py-14 text-center" data-testid="pricing-cta-banner">
          <h3 className="text-3xl md:text-4xl font-semibold text-white mb-4 tracking-tight">{data.cta_title}</h3>
          <p className="text-white/85 text-lg mb-8 max-w-2xl mx-auto">{data.cta_subtitle}</p>
          <Button
            size="lg"
            onClick={() => scrollToSection('contact')}
            className="bg-white hover:bg-white/90 text-[#0071E3] rounded-full px-8 h-12 font-semibold group"
            data-testid="pricing-cta-button"
          >
            {data.cta_button}
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Pricing;
